import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { t } from "i18next";

interface LivisColumnHighchartsProps {
    data: any[];
    height?: number;
    tooltip?: boolean;
    pageSize?: number;
    pointWidth?: number;
    pointWidthDesk?: number;
    pointWidthTab?: number;
    pointWidthMob?: number;
    onClickPoint?: (point: any) => void;
    chartType?: 'single' | 'grouped';
    showLegend?: boolean;
    noDataMessage?: string; // NEW: Custom no data message
    rest?: any;
    borderRadiusTopLeft?: number;
    borderRadiusTopRight?: number;
    x_title?: string;
    y_title?: string;
    yAxisConfig?: any;
}

const DEFAULT_PAGE_SIZE = 5;

const LivisColumnHighcharts = ({
    data,
    height = 250,
    tooltip = true,
    pageSize = DEFAULT_PAGE_SIZE,
    pointWidth = 80,
    pointWidthDesk,
    pointWidthTab,
    pointWidthMob,
    onClickPoint,
    showLegend = false,
    chartType = 'single',
    borderRadiusTopLeft = 8,
    borderRadiusTopRight = 8,
    noDataMessage = "No Record Found", // NEW: Default message
    x_title,
    y_title,
    yAxisConfig,
    ...rest
}: LivisColumnHighchartsProps) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const [page, setPage] = useState(0);
    const [currentPointWidth, setCurrentPointWidth] = useState(pointWidth);

    // Define responsive breakpoints for different screen sizes
    const breakpoints = {
        mobile: 768,    // < 768px
        tablet: 1024,   // 768px - 1023px
        desktop: 1440,  // 1024px - 1439px
        lg: 1920        // >= 1920px
    };

    /**
     * Calculate appropriate pointWidth based on current screen width
     * Uses breakpoints to determine which pointWidth prop to use
     * 
     * @returns {number} The calculated pointWidth for the current screen size
     */
    const getResponsivePointWidth = () => {
        const width = window.innerWidth;

        // Mobile devices
        if (width < breakpoints.mobile && pointWidthMob) {
            return pointWidthMob;
        }
        // Tablet devices
        else if (width >= breakpoints.mobile && width < breakpoints.desktop && pointWidthTab) {
            return pointWidthTab;
        }
        // Desktop devices
        else if (width >= breakpoints.tablet && width > breakpoints.desktop && pointWidthDesk) {
            return pointWidthDesk;
        }
        // Default fallback
        else {
            return pointWidth;
        }
    };

    // Effect to handle window resize events and update pointWidth
    useEffect(() => {
        /**
         * Handle window resize events
         * Updates the current pointWidth state based on new window size
         */
        const handleResize = () => {
            setCurrentPointWidth(getResponsivePointWidth());
        };

        // Set initial pointWidth on component mount
        handleResize();

        // Add resize event listener
        window.addEventListener('resize', handleResize);

        // Cleanup: remove event listener on component unmount
        return () => window.removeEventListener('resize', handleResize);
    }, [pointWidth, pointWidthTab, pointWidthMob]);

    // NEW: Check for empty data and render no data message
    if (!data || data.length === 0) {
        return (
            <div 
                className="livis-dashboard-no-record-centered"
                style={{ 
                    width: '100%', 
                    height: height, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                }}
            >
                <p>{t(noDataMessage)}</p>
            </div>
        );
    }

    // Check if data is grouped (has cameras property)
    const isGroupedData = data.length > 0 && data[0].cameras;
    const actualChartType = isGroupedData ? 'grouped' : chartType;

    // Prepare data based on chart type - properly typed variables
    let chartData: any[];
    let categories: string[];
    let totalPages: number;
    let pagedData: any[];

    if (actualChartType === 'grouped' && isGroupedData) {
        // Grouped column chart logic
        totalPages = Math.ceil(data.length / pageSize);
        pagedData = data.slice(page * pageSize, (page + 1) * pageSize);
        
        // Create categories for x-axis (Recipe names)
        categories = pagedData.map(recipe => recipe.name);
        
        // Create series for each camera
        const cameraNames = pagedData[0]?.cameras?.map((cam: { name: string; value?: number; color?: string }) => cam.name) || [];
        chartData = cameraNames.map((cameraName: string) => ({
            name: cameraName,
            data: pagedData.map(recipe => {
                const camera = recipe.cameras.find((cam: { name: string; value?: number; color?: string }) => cam.name === cameraName);
                return {
                    y: camera?.value || 0,
                    color: camera?.color || '#DC2626',
                    // ensure per-point corner radii are passed to Highcharts
                    borderRadius: [8, 8, 0, 0] as any
                };
            })
        }));
    } else {
        // Single column chart logic (existing functionality)
        totalPages = Math.ceil(data.length / pageSize);
        pagedData = data.slice(page * pageSize, (page + 1) * pageSize);
        categories = pagedData.map(d => d.name);
        chartData = [{
            type: 'column',
            // name: '',
            data: pagedData.map((d, index) => ({
                name: d.name,
                y: d.value,
                color: d.color || '#DC2626',
                // per-point radii to round only top corners
                borderRadius: [8, 8, 0, 0] as any,
                originalIndex: page * pageSize + index
            }))
        }];
    }

    // Chart options
    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            backgroundColor: 'transparent',
            height,
            // Visual styles (font-family, colors, sizes) are handled via external CSS targeting the container
            spacing: [12, 0, 4, 0] // top, right, bottom, left
        },
        title: undefined,
        xAxis: {
            categories: categories,
            lineWidth: 0,
            gridLineWidth: 0,
            labels: {
                    formatter: function() {
                        const item = pagedData[this.pos];
                        const maxLength = 12; // Set the truncation limit
                        const fullText = item?.name || '';
                        let displayText = fullText;
                        if (fullText.length > maxLength) {
                            displayText = fullText.slice(0, maxLength) + '...';
                        }
                        // return HTML that uses class names only; styles are in common_styles.scss
                        // we include data attributes with resolved colors so CSS-independent consumers can read them if needed
                        return `
                            <div class="livis-column-highchart-xaxis-label" style="display: flex; flex-direction: column; align-items: center; color: ${item?.color || 'var(--livis-gray-font-color)'}" data-bar-color="${item?.color || '#DC2626'}">
                                <div class="livis-column-highchart-xaxis-title">${displayText}</div>
                                <div class="livis-column-highchart-xaxis-meta" style="color: var(--livis-gray-font-color)">${item?.value || 0}</div>
                            </div>
                        `;
                    },
                    useHTML: true,
                    y: 20 // Move labels down to create space
            },
            title: {
                text: actualChartType === 'grouped' ? undefined : x_title,
                style: {
                     color: '#64748B',
                    fontSize: '14px',
                    fontWeight: '600'
                },
                align: 'middle',
                // y: 30
                x: 0
                
                
            }
        },
        yAxis: {
            title: {
                text: actualChartType === 'grouped' ? t('Accuracy (%)') as string : y_title,
                style: {
                    color: '#64748B',
                    fontSize: '14px',
                    fontWeight: '600'
                }
            },
            gridLineWidth: 0.5,
            gridLineColor: 'var(--livis-dasboard-card-sub-details-divider-bg, #D1D5DB)',
            labels: {
                style: {
                    color: '#64748B',
                    fontSize: '14px',
                    fontWeight: '500'
                },
                formatter: function () {
                    const val = Number(this.value);
                    if (actualChartType === 'grouped') {
                        return val + '%';
                    }
                    if (val >= 1000) return (val / 1000) + 'k';
                    return String(val);
                }
            },
            min: actualChartType === 'grouped' ? 0 : undefined,
            max: actualChartType === 'grouped' ? 100 : yAxisConfig?.length > 0 ? yAxisConfig[0]?.max : undefined,
        },
        plotOptions: {
            column: {
                // Attempt object form (runtime-supported by Highcharts for per-corner radii)
                // Cast to any so TypeScript accepts the non-number type
                borderRadius: 12,
                //     radiusTopLeft: borderRadiusTopLeft,
                //     radiusTopRight: borderRadiusTopRight,
                //     radiusBottomRight: 0,
                //     radiusBottomLeft: 0
                // } as unknown) as any,
                
                pointWidth: actualChartType === 'grouped' ? 
                    Math.min(currentPointWidth * 0.8, 60) : currentPointWidth,
                maxPointWidth: actualChartType === 'grouped' ? 60 : 180,
                cursor: onClickPoint ? 'pointer' : 'default', // Add cursor pointer
                dataLabels: {
                    enabled: false
                },
                states: {
                    hover: {
                        shadow: true,
                    }
                },
                point: {
                    events: {
                        click: function() {
                            if (onClickPoint) {
                                const clickedData = {
                                    name: this.category || this.name,
                                    value: this.y,
                                    index: this.x,
                                    category: this.category,
                                    series: this.series.name,
                                    point: this
                                };
                                onClickPoint(clickedData);
                            }
                        }
                    }
                },
                groupPadding: actualChartType === 'grouped' ? 0.1 : 0.2,
                pointPadding: actualChartType === 'grouped' ? 0.05 : 0.1
            },
            series: {
                // default color moved to CSS; when not provided, Highcharts will use point colors
                color: actualChartType === 'grouped' ? undefined : 'var(--livis-part-rejected-color)',
                borderWidth: 0,
                borderColor: 'transparent'
            }
        },
        tooltip: {
            enabled: tooltip,
            backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
            borderWidth: 0,
            borderRadius: 8,
            shadow: false,
            style: {
                color: 'var(--livis-white-color)',
                fontSize: '14px'
            },
            pointFormat: actualChartType === 'grouped' ? 
                '<b>{point.y}%</b>' : '<b>{point.y}</b>'
        },
        series: chartData,
        credits: {
            enabled: false
        },
        legend: {
            enabled: showLegend,
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
                color: 'var(--livis-gray-font-color)',
                fontSize: '14px',
                fontWeight: '500'
            },
            symbolHeight: 8,
            symbolWidth: 8,
            symbolRadius: 4,
            itemMarginBottom: 8
        }
    };

    // Pagination controls
    const handlePrev = () => setPage(p => Math.max(0, p - 1));
    const handleNext = () => setPage(p => Math.min(totalPages - 1, p + 1));

    return (
        <div className="livis-reports-analytics-chart-wrapper livis-reports-analytics-column-chart">
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                ref={chartRef}
            />
            {totalPages > 1 && (
                <div className="livis-reports-analytics-pagination-container">
                    <button
                        className={`livis-reports-analytics-pagination-btn${page === 0 ? " disabled" : ""}`}
                        onClick={handlePrev}
                        disabled={page === 0}
                        aria-label={t("Previous") as string}
                    >
                        <ChevronLeft />
                    </button>
                    <button
                        className={`livis-reports-analytics-pagination-btn${page === totalPages - 1 ? " disabled" : ""}`}
                        onClick={handleNext}
                        disabled={page === totalPages - 1}
                        aria-label={t("Next") as string}
                    >
                        <ChevronRight />
                    </button>
                </div>
            )}
        </div>
    );
};

export default LivisColumnHighcharts;